import { useState } from 'react';
import { FONT_BODY, FONT_MONO } from './theme';

/**
 * Parse a typed number for DraftNumberInput: null when empty or outside
 * [min, max]; integers are rounded. A comma counts as a decimal point.
 */
export function parseDraft(text, { min = 0, max = Infinity, integer = false } = {}) {
  const s = String(text ?? '').trim().replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  const v = integer ? Math.round(n) : n;
  return v < min || v > max ? null : v;
}

/**
 * A number field that may be empty while Mo edits it. The old inputs
 * snapped an empty value back to 1, so clearing "1" and typing "4" gave
 * "14". Valid values are committed as they are typed; an empty or invalid
 * field shows the last valid value again on blur (allowEmpty commits null
 * instead, for optional fields).
 */
export function DraftNumberInput({
  t, value, onChange, onBlur, min = 0, max = Infinity, integer = false, allowEmpty = false,
  suffix, placeholder, ariaLabel, style, inputRef,
}) {
  const [draft, setDraft] = useState(null); // the text while focused
  const shown = draft ?? (value === null || value === undefined ? '' : String(value));
  return (
    <div style={{ position: 'relative', minWidth: 0, ...style }}>
      <input ref={inputRef} type="number" inputMode={integer ? 'numeric' : 'decimal'} step="any" min={min}
        aria-label={ariaLabel}
        value={shown}
        placeholder={placeholder != null && placeholder !== '' ? String(placeholder) : undefined}
        onFocus={e => { setDraft(shown); e.target.select?.(); }}
        onChange={e => {
          setDraft(e.target.value);
          const v = parseDraft(e.target.value, { min, max, integer });
          if (v !== null) onChange(v);
          else if (allowEmpty && e.target.value.trim() === '') onChange(null);
        }}
        onBlur={() => { setDraft(null); if (onBlur) onBlur(); }}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{
          width: '100%', height: 40, background: t.inputBg, color: t.text,
          fontFamily: FONT_MONO, fontSize: 14, fontWeight: 600, textAlign: 'center',
          border: `1px solid ${t.inputBorder}`, borderRadius: 8, outline: 'none',
          padding: suffix ? '0 26px 0 6px' : '0 6px',
        }} />
      {suffix && (
        <span style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, color: t.textFaint, fontFamily: FONT_BODY, pointerEvents: 'none',
        }}>{suffix}</span>
      )}
    </div>
  );
}

/** A row of mutually exclusive choices, 40px tall (one-handed). */
export function Segmented({ t, options, value, onChange, ariaLabel }) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} style={{
      display: 'flex', gap: 4, padding: 2, borderRadius: 10, background: t.surfaceAlt,
      border: `1px solid ${t.cardBorder}`, minWidth: 0,
    }}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button key={String(o.value)} role="radio" aria-checked={on} onClick={() => onChange(o.value)} style={{
            flex: 1, minWidth: 0, height: 40, borderRadius: 8, cursor: 'pointer', padding: '0 6px',
            border: `1px solid ${on ? t.accentBorder : 'transparent'}`,
            background: on ? t.accentSoft : 'transparent', color: on ? t.accent : t.textMuted,
            fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', touchAction: 'manipulation',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}
